import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { BelgeKutugu, KullanimOzeti, KutukBelgeSatiri, KutukBelgeYaniti } from "@oasis/domain";
import { api } from "@/lib/api";
import { tarih, useVeri } from "@/lib/veri";
import { useOturum } from "@/lib/oturum";
import { Band, Bos, Buton, Etiket, Hata, Kutu, Modal, Sayfa, alan, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { cn } from "@/lib/cn";
import { bicimDili, cevir } from "@/lib/dil";
import { kullanimMetni } from "@/lib/stok-paket";
import { StokSekmesi } from "@/components/kutuk-stok";
import { BelgeCipleri, KutukBelgeleri } from "@/components/kutuk-belgeleri";
import { GRI_ROZET, TARIH_ALANI, YUVA, gosterDugmesiSinifi, gunGirdisi, taslakGirdisi, taslakTamam, type BelgeTaslagi } from "@/components/kutuk-kullanim";

type AlanTipi = "metin" | "sayi" | "bool" | "tarih" | "liste" | "json" | "secmeli";
/** `tablodaYok`: alan yalnız formda (ör. alet "Brand / model", D-049). */
interface Alan { ad: string; etiket: string; tip: AlanTipi; zorunlu?: boolean; ipucu?: string; secenek?: string[]; doldur?: string[]; tablodaYok?: boolean }
/**
 * `pasifli`: kayıtlar pasife alınabilir (D-047; stok hariç, stokta "Mark finished" var). `belgeKutugu`: kaydın "Documents" bölümü (D-048/D-049).
 * `ekAciklama`: sekme açıklamasının ek cümlesi (kaynakçı / alet tarih kuralı); pencerede de görünür.
 * `gorunenAd` (D-062): anahtarı sistem kimliği olan kütükte (NDT inspectors) kaydın ekranda yazan alanı; öteki kütüklerde null.
 */
interface KutukOzet { kod: string; ad: string; aciklama: string; ekAciklama?: string | null; anahtar: string; gorunenAd?: string | null; alanlar: Alan[]; adet: number; pasifli?: boolean; belgeKutugu?: BelgeKutugu | null }
interface RotaAdimi { id: string; sira: number; ad: string; zorunluBelge: string | null; monitor: string | null }
interface Rota { kod: string; ad: string; aktif: boolean; adimlar: RotaAdimi[] }
interface Liste { kutukler: KutukOzet[]; rotalar: Rota[] }
/** Kütük satırı: model kolonları + pasif bilgisi (D-047), kullanım özeti (çöp kutusu ya da "used in …"), belgeler (D-048/D-049). */
type Satir = Record<string, unknown> & {
  aktif?: boolean;
  pasifKim?: string | null;
  pasifTs?: string | null;
  pasifNot?: string | null;
  kullanim?: KullanimOzeti;
  belgeler?: KutukBelgeSatiri[];
  /** D-074 Faz 2: yerine yenisi gelen belgeler */
  yenisiGelenBelgeler?: KutukBelgeSatiri[];
  kaldirilanBelgeler?: KutukBelgeSatiri[];
  /** Kaynakçı / alet: kaydın tarihi en yeni belgeden geliyor ("from certificate"). */
  tarihBelgeden?: boolean;
};
interface Kayitlar { kod: string; ad: string; aciklama: string; ekAciklama?: string | null; anahtar: string; gorunenAd?: string | null; alanlar: Alan[]; pasifli?: boolean; belgeKutugu?: BelgeKutugu | null; satirlar: Satir[] }

/**
 * D-062: kaydın ekranda yazan adı (pencere başlığı, silme onayı, pasife alma başlığı). Kütükte `gorunenAd` varsa (NDT inspectors:
 * anahtar sistem kimliğidir, ekrana yazılmaz) o alanın değeri; boşsa "—", kimliğe düşülmez. Öteki kütüklerde anahtarın kendisi.
 */
function kayitEtiketi(anahtar: string, gorunenAd: string | null, s: Satir): string {
  if (!gorunenAd) return String(s[anahtar]);
  const ad = s[gorunenAd];
  return typeof ad === "string" && ad.trim() ? ad : "—";
}

/** Kayıt penceresi: form verisi ve (belgeli kütükte) kayıtlı belgeler, kaydedilmemiş belge taslakları, tarihin belgeden gelip gelmediği. */
interface FormDurumu {
  acik: boolean;
  duzenlenen: string | null;
  /** D-062: düzenlenen kaydın başlıkta yazan adı (pencere açılırkenki; `kayitEtiketi`); yeni kayıtta null. */
  etiket: string | null;
  veri: Record<string, unknown>;
  belgeler: KutukBelgeSatiri[];
  yenisiGelenBelgeler: KutukBelgeSatiri[];
  kaldirilanBelgeler: KutukBelgeSatiri[];
  taslaklar: BelgeTaslagi[];
  tarihBelgeden: boolean;
}
const BOS_FORM: FormDurumu = { acik: false, duzenlenen: null, etiket: null, veri: {}, belgeler: [], yenisiGelenBelgeler: [], kaldirilanBelgeler: [], taslaklar: [], tarihBelgeden: false };

const BELGE = [
  { deger: "", ad: cevir("— (no document required)") },
  { deger: "pdf", ad: cevir("PDF Report") },
  { deger: "foto", ad: cevir("Photo") },
  { deger: "form", ad: "Form (fit-up)" },
  { deger: "paso", ad: cevir("Pass record") },
];

/** `secmeli` alanın listesinde "yeni değer yaz" seçeneğinin değeri (gerçek değerlerle karışmasın diye). */
const YENI = "__yeni_deger__";

/** Hücreyi okunur metne çevirir: dizi, bool, tarih ve JSON dahil. */
function goster(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? cevir("yes") : cevir("no");
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString(bicimDili());
  return String(v);
}

/** E-40 Kütükler: WPS, sarf, kaynakçı, NDT inspektörü (D-062), alet, stok, ST, kanonik SN ve rota şablonları. */
export function Kutukler() {
  const { ben } = useOturum();
  const { veri: liste, hata, yenile: listeYenile } = useVeri<Liste>("/kutuk");
  const [sekme, setSekme] = useState("wps");
  const rotaSekmesi = sekme === "rota";
  // D-045: Stock sekmesi kendi verisini çeker (PN satırları ve paketler, kutuk-stok.tsx)
  const stokSekmesi = sekme === "stok";
  const { veri: kayitlar, yenile } = useVeri<Kayitlar>(rotaSekmesi || stokSekmesi ? null : `/kutuk/${sekme}`);
  const [form, setForm] = useState<FormDurumu>(BOS_FORM);
  const [yeniSecim, setYeniSecim] = useState<Record<string, boolean>>({});
  /** `doldur` ile formun kendi doldurduğu değerler (JSON metni): kullanıcı değiştirmediyse ürün değişince yenilenir. */
  const [otoDolu, setOtoDolu] = useState<Record<string, string>>({});
  const [rotaForm, setRotaForm] = useState<{ acik: boolean; kod: string; ad: string; adimlar: RotaAdimi[] } | null>(null);
  const [islem, setIslem] = useState<string | null>(null);
  /** D-047: pasif kayıtlar varsayılan gizli; "Show inactive (n)" ile açılır, sekme değişince kapanır. */
  const [pasifAcik, setPasifAcik] = useState(false);
  /** `etiket` (D-062): başlıkta yazan ad; NDT inspectors'ta anahtar (sistem kimliği) yerine inspektörün adı. */
  const [pasifForm, setPasifForm] = useState<{ anahtar: string; etiket: string; not: string } | null>(null);
  /** Documents bölümünde PDF yükleme / belge ekleme sürerken form kaydedilmez ve kapanmaz (taslak kaybolmasın). */
  const [belgeMesgul, setBelgeMesgul] = useState(false);

  if (!ben?.yonetici) return <Band tip="duvar">{cevir("Registers only open to PM and system administrator.")}</Band>;
  if (hata) return <Hata mesaj={hata} />;
  if (!liste) return <Bos>{cevir("Loading...")}</Bos>;

  const dene = async (f: () => Promise<unknown>) => {
    setIslem(null);
    try { await f(); await yenile(); await listeYenile(); } catch (e) { setIslem(e instanceof Error ? e.message : cevir("Error")); }
  };

  const belgeKutugu = kayitlar?.belgeKutugu ?? null;
  const tarihAlani = belgeKutugu ? TARIH_ALANI[belgeKutugu] : undefined;
  /** D-062: görünen ad alanı kayıt yanıtından okunur; yanıtta yoksa sekme listesindeki aynı tanımdan. */
  const gorunenAd = kayitlar ? (kayitlar.gorunenAd ?? liste.kutukler.find((k) => k.kod === kayitlar.kod)?.gorunenAd ?? null) : null;

  const formAc = (satir?: Satir) => {
    if (!kayitlar) return;
    const veri: Record<string, unknown> = {};
    for (const a of kayitlar.alanlar) {
      const v = satir?.[a.ad];
      veri[a.ad] = a.tip === "json" ? (v ? JSON.stringify(v) : "") : a.tip === "tarih" && typeof v === "string" ? v.slice(0, 10) : a.tip === "liste" ? (Array.isArray(v) ? v : []) : (v ?? (a.tip === "bool" ? false : ""));
    }
    setForm({
      acik: true,
      duzenlenen: satir ? String(satir[kayitlar.anahtar]) : null,
      etiket: satir ? kayitEtiketi(kayitlar.anahtar, gorunenAd, satir) : null,
      veri,
      belgeler: satir?.belgeler ?? [],
      yenisiGelenBelgeler: satir?.yenisiGelenBelgeler ?? [],
      kaldirilanBelgeler: satir?.kaldirilanBelgeler ?? [],
      taslaklar: [],
      tarihBelgeden: Boolean(tarihAlani && satir?.tarihBelgeden),
    });
    setYeniSecim({});
    setOtoDolu({});
    setIslem(null);
  };

  /**
   * D-024: `secmeli` alanda var olan değer seçilince `doldur` alanları aynı değerli son kayıttan kopyalanır.
   * Kullanıcının yazdığı ezilmez; formun önceki seçimden doldurduğu (ve kullanıcının dokunmadığı) değer ise
   * yeni seçime göre yenilenir, "yeni değer" (`v` = null) seçilince boşalır — başka ürünün sınıfı ve rotası
   * yeni lota taşınmasın.
   */
  const secmeliSec = (a: Alan, v: string | null) => {
    if (!kayitlar) return;
    const veri = { ...form.veri, [a.ad]: v ?? "" };
    const kaynak = v === null ? undefined : [...kayitlar.satirlar].reverse().find((s) => s[a.ad] === v);
    const oto = { ...otoDolu };
    for (const d of a.doldur ?? []) {
      const simdiki = veri[d];
      const bos = simdiki === "" || simdiki === null || simdiki === undefined || (Array.isArray(simdiki) && !simdiki.length);
      const formunki = d in oto && JSON.stringify(simdiki) === oto[d];
      if (!bos && !formunki) continue;
      const kaynaktan = kaynak?.[d];
      if (kaynaktan !== null && kaynaktan !== undefined) {
        veri[d] = Array.isArray(kaynaktan) ? [...(kaynaktan as unknown[])] : kaynaktan;
        oto[d] = JSON.stringify(veri[d]);
      } else {
        veri[d] = Array.isArray(simdiki) ? [] : "";
        delete oto[d];
      }
    }
    setOtoDolu(oto);
    setForm({ ...form, veri });
  };

  /**
   * Kayıt ve belgeler: yeni kayıtta taslak belgeler kayıtla birlikte gönderilir; kayıtlı kayıtta önce kayıt güncellenir, sonra kalan
   * taslaklar tek tek eklenir (eklenen taslak listeden düşer; hata olursa kalanlar pencerede kalır). Kaynakçı / alet tarihi sunucuda
   * en yeni belgeden yeniden yazılır (D-049).
   */
  const kaydet = () =>
    dene(async () => {
      if (!kayitlar) return;
      if (form.duzenlenen) {
        const anahtar = form.duzenlenen;
        await api.put(`/kutuk/${kayitlar.kod}/${encodeURIComponent(anahtar)}`, { veri: form.veri });
        if (belgeKutugu) {
          for (const t of form.taslaklar.filter(taslakTamam)) {
            belgeDegisti(await api.post<KutukBelgeYaniti>("/kutuk/belge", { kutuk: belgeKutugu, anahtar, ...taslakGirdisi(t) }));
            setForm((f) => ({ ...f, taslaklar: f.taslaklar.filter((x) => x.kimlik !== t.kimlik) }));
          }
        }
      } else {
        const belgeler = belgeKutugu ? form.taslaklar.filter(taslakTamam).map(taslakGirdisi) : [];
        await api.post(`/kutuk/${kayitlar.kod}`, { veri: form.veri, ...(belgeler.length ? { belgeler } : {}) });
      }
      setForm(BOS_FORM);
    });

  /**
   * Pencerede belge eklenip kaldırılınca ya da işaretlenince (D-074 Faz 2: Superseded / Mark as current) listeler ve (kaynakçı / alet) kaydın güncel tarihi forma yazılır; böylece ardından basılan Save
   * bayat tarih göndermez. Tarih belgeden gelmiyorsa ve önceden de gelmiyorsa kullanıcının yazdığı elle değer korunur.
   */
  const belgeDegisti = (y: KutukBelgeYaniti) => {
    setForm((f) => {
      const tarihYaz = tarihAlani && (y.tarihBelgeden || f.tarihBelgeden);
      return {
        ...f,
        belgeler: y.belgeler,
        yenisiGelenBelgeler: y.yenisiGelenBelgeler,
        kaldirilanBelgeler: y.kaldirilanBelgeler,
        tarihBelgeden: Boolean(tarihAlani && y.tarihBelgeden),
        veri: tarihYaz ? { ...f.veri, [tarihAlani]: gunGirdisi(y.tarih) } : f.veri,
      };
    });
    void yenile();
  };

  const durumYaz = (anahtar: string, aktif: boolean, not?: string) =>
    dene(async () => {
      if (!kayitlar) return;
      await api.post(`/kutuk/${kayitlar.kod}/${encodeURIComponent(anahtar)}/durum`, aktif ? { aktif } : { aktif, not: not?.trim() || null });
      setPasifForm(null);
    });

  /** `kayitAdi` (D-062): onay metninde yazan ad (`kayitEtiketi`); adres her zaman anahtarla kurulur. */
  const kayitSil = (anahtar: string, kayitAdi: string, onayli: boolean) => {
    if (!kayitlar) return;
    if (onayli && !window.confirm(cevir("Delete {0}? It is not used anywhere. This cannot be undone.", kayitAdi))) return;
    void dene(() => api.sil(`/kutuk/${kayitlar.kod}/${encodeURIComponent(anahtar)}`));
  };

  return (
    <Sayfa
      ust={cevir("E-40 · System")}
      baslik={cevir("Registers")}
      alt={cevir("Definition records feeding fit-up, welding and verification screens. This is the first step of real data entry: WPSs, Consumable lots, Welder certificates, NDT inspectors, instruments, stock, standard tasks, canonical SN list and Route templates.")}
    >
      <Hata mesaj={islem} />

      <div className="scroll-thin -mx-3 flex gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:px-0">
        {[...liste.kutukler.map((k) => ({ kod: k.kod, ad: k.ad, adet: k.adet })), { kod: "rota", ad: cevir("Route templates"), adet: liste.rotalar.length }].map((k) => (
          <button
            key={k.kod}
            onClick={() => {
              setSekme(k.kod);
              setPasifAcik(false);
            }}
            aria-pressed={sekme === k.kod}
            className={cn(
              "h-kontrol shrink-0 rounded-full border px-3 text-sm font-medium transition",
              sekme === k.kod ? "border-navy bg-navy text-white dark:text-[#0a141c]" : "border-line bg-surface text-ink-2 hover:border-line-strong",
            )}
          >
            {cevir(k.ad)}
            <span className="ml-1.5 opacity-70 tnum">{k.adet}</span>
          </button>
        ))}
      </div>

      {rotaSekmesi ? (
        <>
          <Kutu
            baslik={cevir("Route templates")}
            aksiyon={<Buton kucuk onClick={() => setRotaForm({ acik: true, kod: "", ad: "", adimlar: [] })}><Plus size={13} /> {cevir("New route")}</Buton>}
            padded={false}
          >
            <ul className="divide-y divide-line">
              {liste.rotalar.map((r) => (
                <li key={r.kod} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="font-mono">{r.kod}</b>
                    <span className="text-ink-2">{r.ad}</span>
                    <Etiket>{r.adimlar.length} {cevir("step")}</Etiket>
                    <span className="ml-auto flex gap-1.5">
                      <Buton kucuk onClick={() => setRotaForm({ acik: true, kod: r.kod, ad: r.ad, adimlar: r.adimlar })}>{cevir("Edit")}</Buton>
                      <Buton kucuk tur="tehlike" onClick={() => dene(() => api.sil(`/kutuk/rota/sablon/${r.kod}`))}>{cevir("Delete")}</Buton>
                    </span>
                  </div>
                  <ol className="mt-2 flex flex-wrap gap-1">
                    {r.adimlar.map((a) => (
                      <li key={a.id} className="rounded border border-line bg-surface-2 px-1.5 py-0.5 text-xs" title={a.monitor ?? undefined}>
                        <span className="font-mono text-2xs text-ink-3">{a.sira + 1}</span> {a.ad}
                        {a.zorunluBelge && <span className="ml-1 text-2xs text-orange">({a.zorunluBelge})</span>}
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ul>
          </Kutu>
          <Band tip="bilgi">{cevir("Route steps build the work chain of the repair Item. Route used in open repair Item cannot be changed; those Items must be closed first.")}</Band>
        </>
      ) : stokSekmesi ? (
        <>
          <StokSekmesi onDegisti={() => void listeYenile()} />
          <Band tip="bilgi">{cevir("There is also an Excel path for bulk entry: export the existing records, edit them, then restore with")} <code>POST /api/ice-aktar/stok</code>. {cevir("Individual editing is here.")}</Band>
        </>
      ) : kayitlar ? (
        (() => {
          const tabloAlanlari = kayitlar.alanlar.filter((a) => !a.tablodaYok);
          const pasifSayisi = kayitlar.pasifli ? kayitlar.satirlar.filter((s) => s.aktif === false).length : 0;
          const gorunen = kayitlar.satirlar.filter((s) => pasifAcik || s.aktif !== false);
          const kolonSayisi = tabloAlanlari.length + (belgeKutugu ? 1 : 0) + (kayitlar.pasifli ? 1 : 0) + 1;
          return (
            <>
              <Kutu
                baslik={cevir(kayitlar.ad)}
                aksiyon={<Buton kucuk tur="birincil" onClick={() => formAc()}><Plus size={13} /> {cevir("Add record")}</Buton>}
                padded={false}
              >
                <p className="border-b border-line px-4 py-2 text-sm text-ink-2">
                  {cevir(kayitlar.aciklama)}
                  {kayitlar.ekAciklama && <> <span>{cevir(kayitlar.ekAciklama)}</span></>}
                </p>
                <div className="scroll-thin overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b border-line">
                        {tabloAlanlari.map((a) => <th key={a.ad} className={tabloBas}>{cevir(a.etiket)}</th>)}
                        {belgeKutugu && <th className={tabloBas}>{cevir("Documents")}</th>}
                        {kayitlar.pasifli && <th className={tabloBas}>{cevir("Status")}</th>}
                        <th className={tabloBas} />
                      </tr>
                    </thead>
                    <tbody>
                      {gorunen.map((s) => {
                        const anahtar = String(s[kayitlar.anahtar]);
                        // D-062: başlık ve onay metinlerinde yazan ad (NDT inspectors'ta anahtar değil, inspektörün adı)
                        const kayitAdi = kayitEtiketi(kayitlar.anahtar, gorunenAd, s);
                        const pasif = s.aktif === false;
                        const kullanim = s.kullanim;
                        return (
                          <tr key={anahtar} className="border-b border-line last:border-0 hover:bg-surface-2">
                            {tabloAlanlari.map((a) => (
                              <td key={a.ad} className={cn(tabloHucre, a.ad === kayitlar.anahtar && "whitespace-nowrap font-mono font-semibold", pasif && "text-ink-3")}>
                                {goster(s[a.ad])}
                                {a.ad === tarihAlani && s.tarihBelgeden && <span className="block whitespace-nowrap text-xs text-ink-3">{cevir("from certificate")}</span>}
                              </td>
                            ))}
                            {belgeKutugu && <td className={tabloHucre}><BelgeCipleri kutuk={belgeKutugu} belgeler={s.belgeler ?? []} /></td>}
                            {kayitlar.pasifli && (
                              <td className={tabloHucre}>
                                {pasif ? (
                                  <span className={GRI_ROZET}>
                                    {cevir("inactive · {0} · {1}", tarih(s.pasifTs), s.pasifKim ?? "—")}
                                    {s.pasifNot ? ` · ${s.pasifNot}` : ""}
                                  </span>
                                ) : (
                                  <span className="text-xs text-ok">{cevir("active")}</span>
                                )}
                              </td>
                            )}
                            <td className={tabloHucre}>
                              <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                                <Buton kucuk onClick={() => formAc(s)}>{cevir("Edit")}</Buton>
                                {kayitlar.pasifli ? (
                                  <>
                                    {pasif ? (
                                      <Buton kucuk onClick={() => durumYaz(anahtar, true)}>{cevir("Activate")}</Buton>
                                    ) : (
                                      <Buton kucuk onClick={() => { setIslem(null); setPasifForm({ anahtar, etiket: kayitAdi, not: "" }); }}>{cevir("Deactivate")}</Buton>
                                    )}
                                    {/* D-047: çöp kutusu yalnız hiçbir yerde kullanılmamış kayıtta ve onayla; kullanılmışta yerinde "used in …" */}
                                    <span className={YUVA}>
                                      {kullanim && kullanim.toplam > 0 ? (
                                        <span className="text-xs text-ink-3">{kullanimMetni(kullanim)}</span>
                                      ) : (
                                        <Buton kucuk tur="tehlike" title={cevir("Delete")} onClick={() => kayitSil(anahtar, kayitAdi, true)}><Trash2 size={13} /></Buton>
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <Buton kucuk tur="tehlike" onClick={() => kayitSil(anahtar, kayitAdi, false)}><Trash2 size={13} /></Buton>
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {!gorunen.length && <tr><td colSpan={kolonSayisi}><Bos>{cevir("No records. Start with \"Add record\".")}</Bos></td></tr>}
                    </tbody>
                  </table>
                </div>
                {kayitlar.pasifli && (
                  <div className="px-4 py-3">
                    <button type="button" aria-pressed={pasifAcik} disabled={!pasifSayisi} onClick={() => setPasifAcik(!pasifAcik)} className={gosterDugmesiSinifi(pasifAcik)}>
                      {cevir("Show inactive ({0})", pasifSayisi)}
                    </button>
                  </div>
                )}
              </Kutu>
              <Band tip="bilgi">{cevir("There is also an Excel path for bulk entry: export the existing records, edit them, then restore with")} <code>POST /api/ice-aktar/{kayitlar.kod}</code>. {cevir("Individual editing is here.")}</Band>
            </>
          );
        })()
      ) : (
        <Bos>{cevir("Loading...")}</Bos>
      )}

      {form.acik && kayitlar && (
        <Modal baslik={form.duzenlenen ? `${cevir(kayitlar.ad)} — ${form.etiket ?? "—"}` : cevir`${cevir(kayitlar.ad)} — new record`} onKapat={() => setForm(BOS_FORM)} genis={belgeKutugu ? "cok" : true} kilitli={belgeMesgul}>
          <Hata mesaj={islem} />
          <div className="grid gap-3 sm:grid-cols-2">
            {kayitlar.alanlar.map((a) => {
              const deger = form.veri[a.ad];
              const yaz = (v: unknown) => setForm({ ...form, veri: { ...form.veri, [a.ad]: v } });
              // D-049: kaynakçı / alet tarihi en yeni belgeden geliyorsa alan kapalı, etiketin yanında "· from certificate"
              const belgeden = a.ad === tarihAlani && form.tarihBelgeden;
              return (
                <div key={a.ad} className={a.tip === "json" ? "sm:col-span-2" : undefined}>
                  <label className={etiket}>
                    {cevir(a.etiket)}
                    {a.zorunlu && <span className="text-bad"> *</span>}
                    {belgeden && <span className="font-normal text-info"> · {cevir("from certificate")}</span>}
                  </label>
                  {a.tip === "bool" ? (
                    <label className="flex h-kontrol items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(deger)} onChange={(e) => yaz(e.target.checked)} /> {cevir(a.etiket)}</label>
                  ) : a.tip === "liste" ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(a.secenek ?? []).map((o) => {
                        const secili = Array.isArray(deger) && deger.includes(o);
                        return (
                          <button
                            key={o}
                            type="button"
                            onClick={() => yaz(secili ? (deger as string[]).filter((x) => x !== o) : [...((deger as string[]) ?? []), o])}
                            className={cn("h-kontrol-kucuk rounded-full border px-2.5 text-sm", secili ? "border-orange bg-orange-soft text-orange" : "border-line text-ink-2")}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  ) : a.tip === "secmeli" ? (
                    (() => {
                      const secenekler = [...new Set(kayitlar.satirlar.map((s) => s[a.ad]).filter((x): x is string => typeof x === "string" && x.trim() !== ""))].sort((x, y) => x.localeCompare(y));
                      const yeniMod = Boolean(yeniSecim[a.ad]) || (typeof deger === "string" && deger !== "" && !secenekler.includes(deger));
                      return (
                        <>
                          <select
                            className={girdi}
                            value={yeniMod ? YENI : String(deger ?? "")}
                            onChange={(e) => {
                              const v = e.target.value;
                              setYeniSecim({ ...yeniSecim, [a.ad]: v === YENI });
                              secmeliSec(a, v === YENI ? null : v);
                            }}
                          >
                            <option value="">—</option>
                            {secenekler.map((o) => <option key={o} value={o}>{o}</option>)}
                            <option value={YENI}>{cevir`+ Add new ${cevir(a.etiket).toLowerCase()}…`}</option>
                          </select>
                          {yeniMod && <input className={cn(girdi, "mt-2")} autoFocus placeholder={a.ipucu ? cevir(a.ipucu) : undefined} value={String(deger ?? "")} onChange={(e) => yaz(e.target.value)} />}
                        </>
                      );
                    })()
                  ) : a.tip === "json" ? (
                    <textarea rows={3} className={cn(alan, "font-mono text-sm")} placeholder={a.ipucu ? cevir(a.ipucu) : undefined} value={String(deger ?? "")} onChange={(e) => yaz(e.target.value)} />
                  ) : a.secenek ? (
                    <select className={girdi} value={String(deger ?? "")} onChange={(e) => yaz(e.target.value)}>
                      <option value="">—</option>
                      {a.secenek.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      className={girdi}
                      type={a.tip === "tarih" ? "date" : a.tip === "sayi" ? "number" : "text"}
                      step="any"
                      placeholder={a.ipucu ? cevir(a.ipucu) : undefined}
                      disabled={(Boolean(form.duzenlenen) && a.ad === kayitlar.anahtar) || belgeden}
                      value={String(deger ?? "")}
                      onChange={(e) => yaz(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {belgeKutugu ? (
            <KutukBelgeleri
              kutuk={belgeKutugu}
              anahtar={form.duzenlenen}
              belgeler={form.belgeler}
              yenisiGelenBelgeler={form.yenisiGelenBelgeler}
              kaldirilanBelgeler={form.kaldirilanBelgeler}
              taslaklar={form.taslaklar}
              onTaslak={(f) => setForm((fm) => ({ ...fm, taslaklar: f(fm.taslaklar) }))}
              onDegisti={belgeDegisti}
              ekAciklama={kayitlar.ekAciklama}
              onMesgul={setBelgeMesgul}
              kayitTarihi={tarihAlani ? String(form.veri[tarihAlani] ?? "") || null : null}
            />
          ) : (
            kayitlar.ekAciklama && <p className="mt-2 text-xs text-ink-3">{cevir(kayitlar.ekAciklama)}</p>
          )}
          <div className="mt-4 flex gap-2">
            <Buton tur="birincil" disabled={belgeMesgul} onClick={kaydet}>{cevir("Save")}</Buton>
            <Buton disabled={belgeMesgul} onClick={() => setForm(BOS_FORM)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {pasifForm && kayitlar && (
        <Modal baslik={cevir("Deactivate {0}?", pasifForm.etiket)} onKapat={() => setPasifForm(null)}>
          <Hata mesaj={islem} />
          <p className="text-sm leading-relaxed text-ink-2">{cevir("Inactive records are not offered in any selection list. Forms and records that already use it keep it and still show it.")}</p>
          <div className="mt-3">
            <label className={etiket}>{cevir("Note (optional)")}</label>
            <textarea rows={2} className={alan} value={pasifForm.not} onChange={(e) => setPasifForm({ ...pasifForm, not: e.target.value })} />
          </div>
          <div className="mt-4 flex gap-2">
            <Buton tur="birincil" onClick={() => durumYaz(pasifForm.anahtar, false, pasifForm.not)}>{cevir("Deactivate")}</Buton>
            <Buton onClick={() => setPasifForm(null)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {rotaForm?.acik && (
        <Modal baslik={rotaForm.kod ? cevir`Route — ${rotaForm.kod}` : cevir("New Route template")} onKapat={() => setRotaForm(null)} genis>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className={etiket}>{cevir("Code")}</label><input className={girdi} placeholder="MB" value={rotaForm.kod} onChange={(e) => setRotaForm({ ...rotaForm, kod: e.target.value })} /></div>
            <div><label className={etiket}>{cevir("Name")}</label><input className={girdi} placeholder="Main barrel" value={rotaForm.ad} onChange={(e) => setRotaForm({ ...rotaForm, ad: e.target.value })} /></div>
          </div>
          <div className="mt-3 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Steps (order matters)")}</div>
            {rotaForm.adimlar.map((a, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_160px_1fr_auto]">
                <input className={girdi} placeholder={cevir("Step name")} value={a.ad} onChange={(e) => setRotaForm({ ...rotaForm, adimlar: rotaForm.adimlar.map((x, k) => (k === i ? { ...x, ad: e.target.value } : x)) })} />
                <select className={girdi} value={a.zorunluBelge ?? ""} onChange={(e) => setRotaForm({ ...rotaForm, adimlar: rotaForm.adimlar.map((x, k) => (k === i ? { ...x, zorunluBelge: e.target.value || null } : x)) })}>
                  {BELGE.map((b) => <option key={b.deger} value={b.deger}>{b.ad}</option>)}
                </select>
                <input className={girdi} placeholder={cevir("Step monitor (optional description)")} value={a.monitor ?? ""} onChange={(e) => setRotaForm({ ...rotaForm, adimlar: rotaForm.adimlar.map((x, k) => (k === i ? { ...x, monitor: e.target.value || null } : x)) })} />
                <Buton kucuk tur="tehlike" onClick={() => setRotaForm({ ...rotaForm, adimlar: rotaForm.adimlar.filter((_, k) => k !== i) })}><Trash2 size={13} /></Buton>
              </div>
            ))}
            <Buton kucuk onClick={() => setRotaForm({ ...rotaForm, adimlar: [...rotaForm.adimlar, { id: `y${rotaForm.adimlar.length}`, sira: rotaForm.adimlar.length, ad: "", zorunluBelge: null, monitor: null }] })}><Plus size={13} /> {cevir("Add step")}</Buton>
          </div>
          <div className="mt-4 flex gap-2">
            <Buton
              tur="birincil"
              disabled={!rotaForm.kod || !rotaForm.ad || !rotaForm.adimlar.length || rotaForm.adimlar.some((a) => !a.ad.trim())}
              onClick={() => dene(async () => { await api.post("/kutuk/rota/sablon", { kod: rotaForm.kod, ad: rotaForm.ad, adimlar: rotaForm.adimlar.map((a) => ({ ad: a.ad, zorunluBelge: a.zorunluBelge, monitor: a.monitor })) }); setRotaForm(null); })}
            >
              {cevir("Save")}
            </Buton>
            <Buton onClick={() => setRotaForm(null)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}
    </Sayfa>
  );
}
